// ═══════════════════════════════════════════════
//   INFRA — cross-cutting helpers used by every feature.
//   Loaded after db.js, before bootstrap.js.
//
//   Exposes (global):
//     SFX.play(type)    — Web Audio sound effects
//     addPts(n)         — mutate S.pts, sync DOM, refresh shop, save
//     toast(msg)        — bottom toast notification
//     fmt(s)            — m:ss formatter
//     todayStr()        — ISO yyyy-mm-dd
// ═══════════════════════════════════════════════

const {S, scheduleSave} = window;

// ── Sound Effects (Web Audio API — no files needed) ──────────────────────────
const SFX = (() => {
    let ctx = null;

    function getCtx() {
        if (ctx) return ctx;
        try {
            ctx = new (window.AudioContext || window.webkitAudioContext)();
        } catch (e) {
            return null;
        }
        return ctx;
    }

    function tone(notes, wave = 'square') {
        const c = getCtx();
        if (!c) return;
        if (c.state === 'suspended') c.resume();
        notes.forEach(([freq, t, dur, vol = 0.12]) => {
            try {
                const osc = c.createOscillator(), gain = c.createGain();
                osc.type = wave;
                osc.connect(gain);
                gain.connect(c.destination);
                osc.frequency.setValueAtTime(freq, c.currentTime + t);
                gain.gain.setValueAtTime(vol, c.currentTime + t);
                gain.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + t + dur);
                osc.start(c.currentTime + t);
                osc.stop(c.currentTime + t + dur + 0.01);
            } catch (e) {
            }
        });
    }

    const S = {
        click: () => tone([[700, 0, .05], [500, .04, .04]]),
        open: () => tone([[440, 0, .04], [660, .04, .04], [880, .08, .07]]),
        close: () => tone([[440, 0, .07]]),
        pts: () => tone([[523, 0, .07], [659, .07, .07], [784, .14, .10]]),
        unlock: () => tone([[523, 0, .08], [659, .08, .08], [784, .16, .08], [1047, .24, .18]]),
        gacha: () => tone([[200, 0, .07], [300, .07, .07], [450, .14, .07], [650, .21, .07], [900, .28, .07], [1200, .35, .22]], 'sawtooth'),
        win: () => tone([[784, 0, .10], [988, .10, .10], [1175, .20, .10], [1568, .30, .25]]),
        tama: () => tone([[880, 0, .05], [1100, .05, .07]]),
        error: () => tone([[250, 0, .10], [200, .10, .15]]),
        rename:   () => tone([[600, 0, .04], [900, .05, .08]]),
        shoot:    () => tone([[1800, 0, .02, .05], [1100, .015, .025, .04]], 'sawtooth'),
        hit:      () => tone([[700, 0, .03, .14], [950, 0, .015, .08], [480, .025, .05, .11]]),
        damage:   () => tone([[200, 0, .09, .18], [160, .07, .13, .15], [220, .14, .12, .10]], 'sawtooth'),
        gameover: () => tone([[392, 0, .14], [330, .17, .14], [262, .34, .14], [196, .52, .28]]),
    };
    return {
        play(type) {
            try {
                (S[type] || S.click)();
            } catch (e) {
            }
        }
    };
})();

function addPts(n, reason) {
    const before = S.pts;
    S.pts = Math.max(0, S.pts + n);
    const applied = S.pts - before; // actual delta after the floor-at-zero clamp
    ['prof-pts', 'tb-pts', 'shop-pts'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.textContent = S.pts;
    });
    const bar = document.getElementById('prof-bar');
    if (bar) bar.style.width = Math.min(100, S.pts / 10) + '%';
    if (n > 0) SFX.play('pts');
    if (window.renderShop) window.renderShop();
    scheduleSave();
    // Append to the immutable audit log. Fire-and-forget — failures don't
    // affect the in-memory balance or the profile save (which carries the
    // canonical pts value as a fallback).
    if (applied !== 0 && window.DB && window.DB.available()) {
        window.DB.getSession().then(({data: {session}}) => {
            if (!session) return;
            // safeCall awaits the supabase-js thenable so the INSERT is
            // actually dispatched; calling .logPtsTransaction without
            // awaiting silently drops the request.
            window.safeCall('logPts',
                () => window.DB.logPtsTransaction(session.user.id, applied, reason || 'unknown', S.pts),
                { toast: false });
        }).catch(() => {});
    }
}

function toast(msg) {
    const t = document.getElementById('toast');
    t.textContent = msg;
    t.style.display = 'block';
    clearTimeout(t._t);
    t._t = setTimeout(() => t.style.display = 'none', 2800);
}

function fmt(s) {
    return Math.floor(s / 60) + ':' + String(Math.floor(s % 60)).padStart(2, '0');
}

function todayStr() {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

// ── Early Bird badge (first 10 users) ──────────────────────────────────────
function earlyBadgeHTML(size = 22) {
    const s = size, sw = Math.max(2, s / 10);
    const pink = '#f72585';
    return `<span class="early-badge" title="Early Visitor ✦ One of the first 10 members!">
    <svg width="${s}" height="${Math.round(s * 0.88)}" viewBox="0 0 64 56" xmlns="http://www.w3.org/2000/svg">
      <!-- Back ear -->
      <path d="M26,18 Q24,6 31,3 Q38,4 37,14"
        fill="none" stroke="${pink}" stroke-width="${sw}" stroke-linecap="round" opacity="0.7"/>
      <!-- Front ear -->
      <path d="M33,16 Q35,4 42,2 Q49,1 47,12 Q46,17 39,17"
        fill="none" stroke="${pink}" stroke-width="${sw}" stroke-linecap="round" stroke-linejoin="round"/>
      <!-- Body outline -->
      <path d="M10,42 Q8,30 14,23 Q23,14 37,15 Q51,15 58,24 Q64,32 60,42 Q55,50 41,52 Q24,53 13,47 Q8,45 10,42Z"
        fill="none" stroke="${pink}" stroke-width="${sw}" stroke-linejoin="round"/>
      <!-- Tail -->
      <circle cx="11" cy="40" r="4.5" fill="none" stroke="${pink}" stroke-width="${sw}"/>
      <!-- Eye -->
      <circle cx="53" cy="29" r="${Math.max(2, s / 14)}" fill="${pink}"/>
      <!-- Nose -->
      <circle cx="44" cy="36" r="${Math.max(1.2, s / 20)}" fill="${pink}" opacity="0.7"/>
    </svg>
  </span>`;
}

function iconFallback(img, emoji) {
    const ph = document.createElement('div');
    ph.className = 'dicon-placeholder';
    ph.textContent = emoji;
    img.replaceWith(ph);
}

// ── Daily mission triggers ──
// Trigger missions auto-complete when the matching in-app event fires.
// Features call this after the action (e.g. tama.js after a feed).
// No-op when there's no active mission with that key today, so it's safe
// to call unconditionally.
function triggerMission(key) {
    if (!key || !S.missionsData || !S.missionsData.length) return;
    const m = S.missionsData.find(x => x.trigger_key === key && !S.completedMissions.includes(x.id));
    if (!m) return;
    S.completedMissions.push(m.id);
    S.missionLinkOpened[m.id] = true;
    if (window.DB && window.DB.available && window.DB.available()) {
        window.safeCall('recordMissionEvent',
            () => window.DB.recordMissionEvent(m.id, 'completed'),
            { toast: false });
    }
    const reward = m.pts || 20;
    addPts(reward, `mission:${m.id}`);
    toast(`Mission complete! +${reward} pts ✦`);
    if (window.renderMissionsList) window.renderMissionsList();
    scheduleSave();
    if (window.unlockNick && S.missionsData.every(x => S.completedMissions.includes(x.id))) {
        window.unlockNick('vip');
    }
}

window.earlyBadgeHTML = earlyBadgeHTML;
window.SFX = SFX;
window.addPts = addPts;
window.toast = toast;
window.fmt = fmt;
window.todayStr = todayStr;
window.iconFallback = iconFallback;
window.triggerMission = triggerMission;
